import type { SearchResultRow } from '../../api/agwa-api-model';
import { getSearchApi } from '../../api/search/search-api';
import { getSseHub } from '../../utility/notifications/sse-hub';

const _entries = new Map<string, SearchResultRow>();
const _listeners = new Map<string, Set<() => void>>();
const _inFlight = new Map<string, Promise<void>>();
const _sseUnsubs = new Map<string, () => void>(); // tag -> unsubscribe
const _tagRefs = new Map<string, number>(); // tag -> subscriber count

function key(tag: string, id: string): string {
  return `${tag}:${id}`;
}

function _notify(k: string) {
  _listeners.get(k)?.forEach((fn) => fn());
}

/** Sync read — returns the cached row or undefined. */
export function getEntry(tag: string, id: string): SearchResultRow | undefined {
  return _entries.get(key(tag, id));
}

/**
 * Push a row directly into the cache (e.g. after a dialog selection where
 * the row is already in hand). Notifies any registered listeners.
 */
export function putEntry(tag: string, id: string, row: SearchResultRow): void {
  const k = key(tag, id);
  _entries.set(k, row);
  _notify(k);
}

/**
 * Evict an entry and notify listeners. Used when a resolve returns 404
 * (entity deleted or access revoked).
 */
export function evictEntry(tag: string, id: string): void {
  const k = key(tag, id);
  _entries.delete(k);
  _notify(k);
}

/**
 * Fetch and cache a single entity. If the entry is already cached the call
 * is a no-op. Concurrent requests for the same key are deduplicated —
 * only one HTTP request fires regardless of how many callers invoke this
 * simultaneously.
 */
export async function requestResolve(tag: string, id: string): Promise<void> {
  const k = key(tag, id);
  if (_entries.has(k)) return;
  await _fetchAndCache(tag, id, k);
}

/**
 * Force a re-fetch even if the entry is currently cached (used by the SSE
 * handler after receiving a tombstone for this key).
 */
export async function invalidateAndRefetch(
  tag: string,
  id: string
): Promise<void> {
  const k = key(tag, id);
  _entries.delete(k);
  await _fetchAndCache(tag, id, k);
}

async function _fetchAndCache(
  tag: string,
  id: string,
  k: string
): Promise<void> {
  if (_entries.has(k)) return;

  if (_inFlight.has(k)) {
    await _inFlight.get(k);
    return;
  }

  const promise = (async () => {
    try {
      const result = await getSearchApi().resolve(tag, id);
      if (result.success) {
        _entries.set(k, result.data);
      } else {
        _entries.delete(k);
      }
    } catch {
      _entries.delete(k);
    } finally {
      _inFlight.delete(k);
      _notify(k);
    }
  })();

  _inFlight.set(k, promise);
  await promise;
}

/**
 * Register a listener for a specific entity key. The listener is called
 * whenever the cache entry for this key changes (resolved, evicted, or
 * updated via SSE).
 *
 * Opens a per-tag SSE subscription on the first subscriber for that tag;
 * closes it when the last subscriber unregisters.
 */
export function subscribe(tag: string, id: string, listener: () => void): void {
  const k = key(tag, id);
  if (!_listeners.has(k)) {
    _listeners.set(k, new Set());
  }
  _listeners.get(k)!.add(listener);

  const count = (_tagRefs.get(tag) ?? 0) + 1;
  _tagRefs.set(tag, count);
  if (count === 1) {
    _openSse(tag);
  }
}

/**
 * Unregister a previously registered listener. Closes the SSE subscription
 * for the tag when no listeners remain.
 */
export function unsubscribe(
  tag: string,
  id: string,
  listener: () => void
): void {
  const k = key(tag, id);
  const set = _listeners.get(k);
  if (set) {
    set.delete(listener);
    if (set.size === 0) {
      _listeners.delete(k);
    }
  }

  const count = Math.max(0, (_tagRefs.get(tag) ?? 0) - 1);
  if (count === 0) {
    _tagRefs.delete(tag);
    _closeSse(tag);
  } else {
    _tagRefs.set(tag, count);
  }
}

function _openSse(tag: string): void {
  // getSseHub() throws a specific "SseHub not initialized" Error when
  // called before app bootstrap completes. In that case we skip the
  // subscription — direct resolve still works, just without live updates.
  // Any OTHER error (subscribeTopic blowing up, type errors, etc.) is a
  // programming bug and must surface — don't catch indiscriminately.
  let hub: ReturnType<typeof getSseHub>;
  try {
    hub = getSseHub();
  } catch (err) {
    if (
      err instanceof Error &&
      err.message.startsWith('SseHub not initialized')
    ) {
      return;
    }
    throw err;
  }
  const unsub = hub.subscribeTopic(`search:${tag}`, (evt) => {
    const data = evt.data as { id?: string };
    if (!data?.id) return;
    const k = key(tag, data.id);
    // Only re-fetch if something in-scope cares about this id.
    if (_entries.has(k) || _listeners.has(k)) {
      void invalidateAndRefetch(tag, data.id);
    }
  });
  _sseUnsubs.set(tag, unsub);
}

function _closeSse(tag: string): void {
  _sseUnsubs.get(tag)?.();
  _sseUnsubs.delete(tag);
}
